import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mutationNotif } from '#/utils/mutationNotif';
import {
  appendOperatorNote,
  chargeTicket,
  completeTicket,
  exportPendingTickets,
  getTicketById,
  listTickets,
  markNoFee,
  markQueryFailed,
  exportCompletedTicket,
  uploadTransactionReceipt,
  getTransactionReceiptDownload,
} from './tickets';
import type {
  AddOperatorNoteBody,
  UploadTransactionReceiptBody,
  ChargeTicketBody,
  CompleteTicketBody,
  ExportTicketsParams,
  ListTicketsParams,
  ExportCompletedTicketParams,
} from './tickets';
import { EtcTicketSchema, EtcTicketSummarySchema } from '../model';
import { fetchAllPages } from '../../utils';

export const ETC_TICKET_QUERY_NAMESPACE = 'etcTicket' as const;

// === Queries ===

export function useEtcTickets(params?: ListTicketsParams) {
  return useQuery({
    queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list', params],
    queryFn: async () => {
      const res = await fetchAllPages(listTickets, { ...params });
      const validData = res.data.filter((item) => {
        const parsed = EtcTicketSummarySchema.safeParse(item);
        if (!parsed.success) {
          console.error(
            '[useEtcTickets] invalid item removed',
            item,
            parsed.error,
          );
        }
        return parsed.success;
      });
      return { ...res, data: validData };
    },
  });
}

export function useEtcTicket(id?: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
    enabled: !!id && enabled,
    queryFn: async () => {
      if (!id) return null;
      const res = await getTicketById(id);
      const parsed = EtcTicketSchema.safeParse(res);
      if (!parsed.success) {
        console.error(
          '[useEtcTicket] invalid response, returning null',
          res,
          parsed.error,
        );
        return null;
      }
      return parsed.data;
    },
  });
}

// === Mutations ===
export function useExportEtcPendingTickets() {
  const NOTIF_ID = 'etc-pending-tickets-export';
  return useMutation({
    mutationFn: (params?: ExportTicketsParams) => exportPendingTickets(params),
    onMutate: () => {
      mutationNotif.loading(NOTIF_ID, {
        title: 'ETC 通行費查繳清單',
        message: '正在匯出 ETC 通行費查繳清單',
      });
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      mutationNotif.toSuccess(NOTIF_ID, '已匯出 ETC 通行費查繳清單');
    },
    onError: () => {
      mutationNotif.toError(NOTIF_ID, '資料匯出時發生問題，請稍後再試。');
    },
  });
}

// === Mutations ===
export function useExportCompletedTicket() {
  const NOTIF_ID = 'etc-completed-tickets-export';
  return useMutation({
    mutationFn: (params: ExportCompletedTicketParams) =>
      exportCompletedTicket(params),
    onMutate: () => {
      mutationNotif.loading(NOTIF_ID, {
        title: 'ETC 通行費已繳清單',
        message: '正在匯出 ETC 通行費已繳清單',
      });
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      mutationNotif.toSuccess(NOTIF_ID, '已匯出 ETC 通行費已繳清單');
    },
    onError: () => {
      mutationNotif.toError(NOTIF_ID, '資料匯出時發生問題，請稍後再試。');
    },
  });
}

export function useMarkNoFee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      markNoFee(id, { reason }),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, {
        title: '標記無需收費',
        message: '標記中...',
      });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已標記為無需收費');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useMarkQueryFailed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      markQueryFailed(id, { reason }),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, {
        title: '標記查詢失敗',
        message: '標記中...',
      });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已標記查詢失敗');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useChargeTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ChargeTicketBody) =>
      chargeTicket(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '標記請款', message: '標記中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '請款成功');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(
        context.notifId,
        '請款失敗！請設定是否需要重新排程',
      );
    },
  });
}

export function useCompleteTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CompleteTicketBody) =>
      completeTicket(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '確認代繳', message: '確認中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已確認代繳完成');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useAddOperatorNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AddOperatorNoteBody) =>
      appendOperatorNote(id, body),
    onMutate: ({ id }) => {
      mutationNotif.loading(id, { title: '新增備註', message: '新增中...' });
      return { notifId: id };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '備註已新增');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useUploadTransactionReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string } & UploadTransactionReceiptBody) =>
      uploadTransactionReceipt(id, body),
    onMutate: ({ id }) => {
      const notifId = `${id}-transaction-receipt`;
      mutationNotif.loading(notifId, {
        title: '上傳交易明細',
        message: '上傳中...',
      });
      return { notifId };
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [ETC_TICKET_QUERY_NAMESPACE, 'item', id],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '交易明細已上傳');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(
        context.notifId,
        '交易明細上傳失敗，請稍後於歷史清單重新上傳',
      );
    },
  });
}

export function useDownloadTransactionReceipt() {
  return useMutation({
    mutationFn: (id: string) => getTransactionReceiptDownload(id),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener');
    },
  });
}
